import { useCallback, useEffect, useRef } from 'react';
import { useApp } from '@context/AppContext';
import { useFirebase } from '@/App';
import { captureCanvas } from '@utils/canvasSnapshot';
import { uploadGameImage, buildSubmissionPath } from '@utils/imageStorage';
import { getAssignment } from './telephoneRotation';
import challengePacks from '@/data/challengePacks.json';

/**
 * Hook providing all Broken Telephone game actions.
 * Host-driven phases — the host advances rounds when all submit or timer expires.
 */
export function useTelephone() {
  const { state, dispatch } = useApp();
  const firebase = useFirebase();
  const { user, room, game } = state;
  const advancingRef = useRef(false);

  const members = Object.keys(room.members || {});

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /** Pick N random prompts from challenge packs (one per player). */
  const pickPrompts = useCallback((count) => {
    const allChallenges = challengePacks.packs.flatMap(p => p.challenges);
    const shuffled = [...allChallenges].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, count);
  }, []);

  /** Shuffle an array (Fisher-Yates). */
  const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  // ---------------------------------------------------------------------------
  // Lobby
  // ---------------------------------------------------------------------------

  /** Host: initialise telephone game state. */
  const initTelephone = useCallback(async ({ drawDuration = 60, guessDuration = 30 } = {}) => {
    const chainOrder = shuffle(members);
    const prompts = pickPrompts(chainOrder.length);

    const chains = {};
    chainOrder.forEach((uid, idx) => {
      chains[uid] = {
        prompt: prompts[idx] || `Mystery doodle #${idx + 1}`,
        steps: {},
      };
    });

    await firebase.updateGameState({
      mode: 'telephone',
      phase: 'lobby',
      drawDuration,
      guessDuration,
      chainOrder,
      totalRounds: chainOrder.length,
      currentRound: 0,
      chains,
      submissions: null,
      revealChain: null,
      revealStep: -1,
      timerEndsAt: null,
      timerDuration: null,
    });
  }, [firebase, members, pickPrompts]);

  // ---------------------------------------------------------------------------
  // Phase transitions
  // ---------------------------------------------------------------------------

  /** Host: start the first drawing round (round 0). */
  const startTelephone = useCallback(async () => {
    const duration = game.drawDuration || 60;
    await firebase.updateGameState({
      phase: 'drawing',
      currentRound: 0,
      timerDuration: duration,
      timerEndsAt: Date.now() + duration * 1000,
      submissions: null,
    });
  }, [firebase, game.drawDuration]);

  /** Host: advance to the next round (or reveal if done). */
  const advanceRound = useCallback(async () => {
    if (advancingRef.current) return;
    advancingRef.current = true;

    try {
      const nextRound = (game.currentRound ?? 0) + 1;
      const total = game.totalRounds || members.length;

      if (nextRound >= total) {
        // All rounds complete — go to reveal
        await firebase.updateGameState({
          phase: 'reveal',
          timerEndsAt: null,
          timerDuration: null,
          submissions: null,
          revealChain: game.chainOrder?.[0] || null,
          revealStep: -1,
        });
      } else {
        // Alternate between drawing and guessing
        const nextType = nextRound % 2 === 0 ? 'drawing' : 'guessing';
        const duration = nextType === 'drawing'
          ? (game.drawDuration || 60)
          : (game.guessDuration || 30);

        await firebase.updateGameState({
          phase: nextType,
          currentRound: nextRound,
          timerDuration: duration,
          timerEndsAt: Date.now() + duration * 1000,
          submissions: null,
        });
      }
    } finally {
      advancingRef.current = false;
    }
  }, [firebase, game.currentRound, game.totalRounds, game.chainOrder, game.drawDuration, game.guessDuration, members.length]);

  // ---------------------------------------------------------------------------
  // Player submissions
  // ---------------------------------------------------------------------------

  /** Player: submit a drawing for the current round. */
  const submitDrawing = useCallback(async (stageRef) => {
    const dataUrl = captureCanvas(stageRef);
    if (!dataUrl) return;

    const chainOrder = game.chainOrder || [];
    const assignment = getAssignment(chainOrder, game.currentRound ?? 0, user.id);
    if (!assignment) return;

    // Upload to Storage, get URL
    const path = buildSubmissionPath(state.room.code,
      `telephone/${assignment.chainStarterId}/step${assignment.stepIndex}`, user.id);
    const imageUrl = await uploadGameImage(dataUrl, path);
    if (!imageUrl) return;

    // Write URL (not base64) to the chain
    await firebase.updateGameState({
      [`chains/${assignment.chainStarterId}/steps/${assignment.stepIndex}`]: {
        type: 'draw',
        userId: user.id,
        imageData: imageUrl,
        round: game.currentRound ?? 0,
      },
      [`submissions/${user.id}`]: true,
    });

    // Exit solo mode and clean up
    dispatch({ type: 'CLEAR_SOLO_STROKES' });
    if (state.canvas.soloMode) {
      dispatch({ type: 'TOGGLE_SOLO_MODE' });
    }
  }, [firebase, dispatch, game.chainOrder, game.currentRound, user.id, state.room.code, state.canvas.soloMode]);

  /** Player: submit a text guess for the current round. */
  const submitGuess = useCallback(async (text) => {
    const chainOrder = game.chainOrder || [];
    const assignment = getAssignment(chainOrder, game.currentRound ?? 0, user.id);
    if (!assignment) return;

    await firebase.updateGameState({
      [`chains/${assignment.chainStarterId}/steps/${assignment.stepIndex}`]: {
        type: 'guess',
        userId: user.id,
        text: (text || '(no guess)').slice(0, 200),
        round: game.currentRound ?? 0,
      },
      [`submissions/${user.id}`]: true,
    });
  }, [firebase, game.chainOrder, game.currentRound, user.id]);

  // ---------------------------------------------------------------------------
  // Auto-advance: all submitted or timer expired (host only)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!user.isHost) return;
    if (game.phase !== 'drawing' && game.phase !== 'guessing') return;

    const submissions = game.submissions || {};
    const submittedCount = Object.keys(submissions).length;
    const totalPlayers = members.length;

    if (submittedCount > 0 && submittedCount >= totalPlayers) {
      // Small delay so the last submission visually registers
      const timeout = setTimeout(() => advanceRound(), 1000);
      return () => clearTimeout(timeout);
    }
  }, [user.isHost, game.phase, game.submissions, members.length, advanceRound]);

  // ---------------------------------------------------------------------------
  // Reveal navigation
  // ---------------------------------------------------------------------------

  /** Host: start the reveal phase (called from the hook if needed externally). */
  const startReveal = useCallback(async () => {
    await firebase.updateGameState({
      phase: 'reveal',
      timerEndsAt: null,
      timerDuration: null,
      revealChain: game.chainOrder?.[0] || null,
      revealStep: -1,
    });
  }, [firebase, game.chainOrder]);

  /** Host: advance to the next step within the current reveal chain. */
  const advanceRevealStep = useCallback(async () => {
    const nextStep = (game.revealStep ?? -1) + 1;
    const chainId = game.revealChain;
    const chain = game.chains?.[chainId];
    if (!chain) return;

    // Total steps = totalRounds (one step per round)
    const totalSteps = game.totalRounds || 0;
    if (nextStep >= totalSteps) return; // use advanceRevealChain instead

    await firebase.updateGameState({ revealStep: nextStep });
  }, [firebase, game.revealStep, game.revealChain, game.chains, game.totalRounds]);

  /** Host: advance to the next chain in the reveal. */
  const advanceRevealChain = useCallback(async () => {
    const chainOrder = game.chainOrder || [];
    const currentIdx = chainOrder.indexOf(game.revealChain);
    const nextIdx = currentIdx + 1;

    if (nextIdx >= chainOrder.length) return; // no more chains

    await firebase.updateGameState({
      revealChain: chainOrder[nextIdx],
      revealStep: -1,
    });
  }, [firebase, game.chainOrder, game.revealChain]);

  // ---------------------------------------------------------------------------
  // End / replay
  // ---------------------------------------------------------------------------

  /** Host: play again with fresh chains and prompts. */
  const playAgain = useCallback(async () => {
    const chainOrder = shuffle(members);
    const prompts = pickPrompts(chainOrder.length);

    const chains = {};
    chainOrder.forEach((uid, idx) => {
      chains[uid] = {
        prompt: prompts[idx] || `Mystery doodle #${idx + 1}`,
        steps: {},
      };
    });

    // Restore shared canvas if we saved it
    if (state.canvas.savedSharedStrokes) {
      dispatch({ type: 'RESTORE_SHARED_STROKES' });
    }

    await firebase.updateGameState({
      phase: 'lobby',
      chainOrder,
      totalRounds: chainOrder.length,
      currentRound: 0,
      chains,
      submissions: null,
      revealChain: null,
      revealStep: -1,
      timerEndsAt: null,
      timerDuration: null,
    });
  }, [firebase, dispatch, members, pickPrompts, state.canvas.savedSharedStrokes]);

  /** Host: end the game entirely. */
  const endGame = useCallback(async () => {
    if (state.canvas.savedSharedStrokes) {
      dispatch({ type: 'RESTORE_SHARED_STROKES' });
    }
    await firebase.clearGame();
    dispatch({ type: 'CLEAR_GAME' });
    dispatch({ type: 'ADD_TOAST', payload: { message: 'Game ended — back to free draw' } });
  }, [firebase, dispatch, state.canvas.savedSharedStrokes]);

  return {
    // Lobby
    initTelephone,
    startTelephone,
    // Submissions
    submitDrawing,
    submitGuess,
    // Host round management
    advanceRound,
    // Reveal
    startReveal,
    advanceRevealStep,
    advanceRevealChain,
    // End
    playAgain,
    endGame,
  };
}
